import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, UserRound, Phone } from "lucide-react";
import { NAV, CONTACT } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";

export const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
    <img src="/images/logo-b2r-gold.png" alt="B2R LLC logo" className="h-11 w-auto" />
    <span className="leading-none whitespace-nowrap">
      <span className="block font-display text-xl tracking-wide">Baggs 2 Riches</span>
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mt-1">LLC · All 50 States</span>
    </span>
  </Link>
);

const linkCls = ({ isActive }) =>
  `text-[12px] tracking-[0.1em] uppercase whitespace-nowrap transition-colors duration-300 ${isActive ? "text-[#D4AF37]" : "text-white/75 hover:text-white"}`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const accountTo = user?.role === "admin" ? "/admin" : user ? "/portal" : "/login";

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-link-${n.label.toLowerCase()}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <Link to={accountTo} className="btn-outline-gold !py-2.5 !px-4 !tracking-[0.1em] whitespace-nowrap" data-testid="nav-account-link">
            <UserRound className="w-4 h-4" /> {user ? "My Portal" : "Client Portal"}
          </Link>
          <Link to="/book" className="btn-gold !py-2.5 !px-5 !tracking-[0.1em] whitespace-nowrap" data-testid="nav-book-button">Book Consultation</Link>
        </div>
        <button className="xl:hidden text-white w-11 h-11 -mr-2 flex items-center justify-center rounded-full active:bg-white/10" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t border-white/10 bg-black/95 px-6 py-4 flex flex-col h-[calc(100svh-5rem)] overflow-y-auto" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={(s) => `${linkCls(s)} !text-sm py-4 border-b border-white/10`} data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}>{n.label}</NavLink>
          ))}
          <NavLink to={accountTo} className={(s) => `${linkCls(s)} !text-sm py-4 border-b border-white/10`} data-testid="nav-mobile-account">{user ? "My Portal" : "Client Portal"}</NavLink>
          <Link to="/book" className="btn-gold mt-8" data-testid="nav-mobile-book">Book Consultation</Link>
          <a href={CONTACT.phoneHref} className="btn-outline-gold mt-3" data-testid="nav-mobile-call"><Phone className="w-4 h-4" /> Call {CONTACT.phone}</a>
        </div>
      )}
    </header>
  );
};
